import { Download, FileText } from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';

interface ResourcesDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onDownloadDocument: (documentType: 'framework' | 'scope3') => void;
}

export function ResourcesDialog({ isOpen, onClose, onDownloadDocument }: ResourcesDialogProps) {
    const resources = [
        {
            id: 'framework',
            title: 'Differentiation Framework Statements',
            description: 'Comprehensive framework statements covering Circular Economy, Supply Chain Innovation, Environmental Impact, and Sustainability Leadership.',
            icon: FileText,
            type: 'framework' as const
        },
        {
            id: 'scope3',
            title: 'Scope 3 Emission Factors for Reporting',
            description: 'Detailed emission factors and conversion rates for all sustainability initiatives including reusable pallets, transport, supplier integration, and cycle time reduction.',
            icon: FileText,
            type: 'scope3' as const
        }
    ];

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-2xl">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Download className="w-6 h-6 text-[#0072CE]" />
                        Download Resources
                    </DialogTitle>
                    <DialogDescription>
                        Download framework statements and emission factors for reporting
                    </DialogDescription>
                </DialogHeader>

                <div className="mt-6 space-y-4">
                    {resources.map((resource) => {
                        const Icon = resource.icon;
                        return (
                            <div
                                key={resource.id}
                                className="p-5 bg-white border border-gray-200 rounded-lg hover:shadow-md transition-all duration-200 group"
                            >
                                <div className="flex items-start gap-4">
                                    <div className="flex-shrink-0 w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center text-[#0072CE] group-hover:bg-blue-100 transition-colors">
                                        <Icon className="w-6 h-6" />
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="text-gray-900 mb-2">
                                            {resource.title}
                                        </h3>
                                        <p className="text-sm text-gray-600 mb-4">{resource.description}</p>
                                        <Button
                                            onClick={() => onDownloadDocument(resource.type)}
                                            className="bg-[#0072CE] hover:bg-[#0251bf] text-white gap-2"
                                            size="sm"
                                        >
                                            <Download className="w-4 h-4" />
                                            Download CSV
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-gray-700">
                        <strong>Note:</strong> These documents contain official P&G-CHEP sustainability data and emission factors for ESG reporting purposes.
                    </p>
                </div>
            </DialogContent>
        </Dialog>
    );
}
